import type { UpdateReportData } from "../api/services.types.ts"
import type { ReportListItem } from "../types/types.ts"

export type ReportSettingsSource = Pick<
  ReportListItem,
  "name" | "description" | "tags" | "folder"
>

/** The four metadata fields both the create wizard and the settings modal author. */
export interface ReportSettingsValues {
  name: string
  description: string
  folder: string
  tags: string[]
}

export type ReportSettingsPatch = Pick<
  UpdateReportData,
  "name" | "description" | "folder" | "tags"
>

// `Select mode="tags"` has no maxLength of its own, so the column's limit is
// applied here — the picker shows the capped value straight back.
export const FOLDER_MAX_LENGTH = 100

// The picker is a tags-mode Select, so a single-folder report arrives as an array.
export const folderFromSelection = (selection: string[]): string =>
  selection.length
    ? selection[selection.length - 1].trim().slice(0, FOLDER_MAX_LENGTH)
    : ""

export const folderSelection = (folder: string): string[] =>
  folder ? [folder] : []

export const reportSettingsFormValues = (
  report: ReportSettingsSource
): ReportSettingsValues => ({
  name: report.name,
  description: report.description ?? "",
  folder: report.folder ?? "",
  tags: report.tags ?? []
})

const sameTags = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((tag, index) => tag === right[index])

const cleanTags = (tags: string[]): string[] =>
  tags.map((tag) => tag.trim()).filter(Boolean)

/** Only the fields the user actually changed, so a save never rewrites a column
 * it did not touch — and an unchanged form produces no request at all.
 *
 * Nothing trims on the way in, so a stored value can carry whitespace the form
 * strips. Both sides are normalised before comparing, or simply opening the
 * modal and pressing Save would rewrite a field nobody edited. */
export const buildReportSettingsPatch = (
  report: ReportSettingsSource,
  values: ReportSettingsValues
): ReportSettingsPatch => {
  const name = values.name.trim()
  const description = values.description.trim()
  const folder = values.folder.trim()
  const tags = cleanTags(values.tags)

  const patch: ReportSettingsPatch = {}
  if (name !== report.name.trim()) patch.name = name
  if (description !== (report.description ?? "").trim())
    patch.description = description
  if (folder !== (report.folder ?? "").trim()) patch.folder = folder
  if (!sameTags(tags, cleanTags(report.tags ?? []))) patch.tags = tags
  return patch
}
